/**
 * AppSignal Redux Middleware - Using Breadcrumbs
 *
 * This middleware integrates AppSignal error monitoring with Redux using breadcrumbs
 * to track the sequence of actions that led to an error.
 *
 * Features:
 * - Creates a breadcrumb for every Redux action
 * - Tracks action sequence (chronological history of user's path through the app)
 * - Automatically includes user context (ID only - PII compliant)
 * - Breadcrumbs are sent with every error report automatically
 * - Safe error handling - middleware failures never break the application
 *
 * How it works:
 * - Intercepts all Redux actions in the middleware chain
 * - Creates an AppSignal breadcrumb for each action with metadata
 * - When an error occurs, AppSignal automatically includes the breadcrumb trail
 * - Error handlers can also access current state via getReduxState() for detailed inspection
 *
 * Performance considerations:
 * - Breadcrumbs are lightweight (just action type + minimal metadata)
 * - No state serialization on every action (only when error occurs)
 * - All AppSignal calls are wrapped in try-catch to prevent middleware failures
 *
 * PII Compliance:
 * - Only user IDs are included (no names or emails)
 * - Follows AppSignal best practices for PII handling
 *
 * Testing locally:
 * - Set REACT_APP_APPSIGNAL_KEY in .env to enable AppSignal
 * - Use dev key: fc66a698-9360-4ef8-b2a3-eb4ddf38a791 (sends to "dev" environment)
 * - Check browser console for AppSignal initialization messages
 * - Trigger actions and errors to verify breadcrumbs appear in AppSignal dashboard
 */

import appsignal from "lib/appsignal";

/**
 * Store reference to the Redux store so error handlers can access current state
 * This is set by the middleware and used by error handlers
 */
let reduxStoreRef = null;

/**
 * Get the current Redux state for error reporting
 * This is called by error handlers to include full state in error reports
 *
 * @returns {Object|null} Current Redux state or null if not available
 */
export const getReduxState = () => {
  try {
    if (!reduxStoreRef) {
      return null;
    }
    return reduxStoreRef.getState();
  } catch (error) {
    console.error("Failed to get Redux state", error);
    return null;
  }
};

/**
 * Safely create a clean copy of state for error reporting
 *
 * Handles:
 * - Circular references (replaces with "[Circular]")
 * - Non-serializable values (functions, symbols, etc.)
 * - Creates a clean object that can be safely used by error handlers
 *
 * @param {Object} state - Redux state to clean
 * @returns {Object} Clean state object
 */
export const cleanStateForErrorReporting = state => {
  try {
    // Track seen objects to detect circular references
    const seen = new WeakSet();

    const replacer = (key, value) => {
      // Handle null and primitives
      if (value === null || typeof value !== "object") {
        return value;
      }

      // Detect circular references
      if (seen.has(value)) {
        return "[Circular]";
      }

      seen.add(value);
      return value;
    };

    // Serialize and parse to create a clean copy
    const serialized = JSON.stringify(state, replacer);
    return JSON.parse(serialized);
  } catch (error) {
    console.error("AppSignal Middleware: Failed to clean state", error);
    return { error: "[State Cleaning Error]" };
  }
};

/**
 * Redux middleware that adds breadcrumbs for every action
 *
 * Uses AppSignal breadcrumbs to track the sequence of Redux actions.
 * When an error occurs, breadcrumbs are automatically included in the error report,
 * showing the trail of actions that led to the error.
 *
 * Middleware signature: store => next => action => result
 *
 * This middleware:
 * 1. Stores a reference to the Redux store (for error handlers)
 * 2. Processes the action through the Redux chain
 * 3. Adds a breadcrumb with action type and minimal metadata
 * 4. Never breaks the application even if breadcrumb creation fails
 *
 * @param {Object} store - Redux store with getState() method
 * @returns {Function} Next middleware function
 */
const appsignalMiddleware = store => next => action => {
  try {
    // Store reference to Redux store so error handlers can access state
    if (!reduxStoreRef) {
      reduxStoreRef = store;
    }

    // Continue the action chain and capture result
    const result = next(action);

    // Add breadcrumb after action completes (in a try-catch in case it fails)
    try {
      const state = store.getState();
      const currentUser = state?.currentUser;
      const router = state?.router;
      const currentTeam = state?.ui?.currentTeam;

      // Serialize action data for breadcrumb (excluding type and internal props)
      let actionData = "";
      try {
        // Create a clean copy of the action, excluding type and CALL_API symbol
        const { type, ...actionProps } = action;

        // Remove internal properties that aren't useful for debugging
        const cleanAction = { ...actionProps };
        delete cleanAction.onSuccess;
        delete cleanAction.onFailure;
        delete cleanAction[Symbol.for("@@redux-saga/SAGA_ACTION")];

        // Only serialize if there's actually data
        const keys = Object.keys(cleanAction);
        if (keys.length > 0) {
          const serialized = JSON.stringify(cleanAction);
          // Truncate to 1000 chars to keep breadcrumbs manageable
          actionData =
            serialized.length > 1000
              ? serialized.substring(0, 1000) + "... [truncated]"
              : serialized;
        }
      } catch (serializeError) {
        actionData = "[Cannot Serialize]";
      }

      // Create breadcrumb with action type, data, and metadata
      // Metadata must be <String, String> per AppSignal docs
      appsignal.addBreadcrumb({
        category: "redux",
        action: action.type || "[No Type]",
        message: actionData || undefined, // Action data as message (omit if empty)
        metadata: {
          // User context
          user_id: String(currentUser?.id || "not_logged_in"),
          user_name: currentUser?.name || "not_logged_in",
          user_email: currentUser?.email || "not_logged_in",

          // Include key navigation/context info
          current_path: router?.location?.pathname || "",
          current_team_id: String(currentTeam?.id || ""),

          // Include action error if present (for failed actions)
          action_error: action.error ? String(action.error) : "",

          // Timestamp for this breadcrumb
          timestamp: new Date().toISOString()
        }
      });
    } catch (breadcrumbError) {
      // Breadcrumb errors should never break the application
      // Log silently in development only
      if (process.env.NODE_ENV === "development") {
        console.warn(
          "AppSignal Middleware: Failed to add breadcrumb",
          breadcrumbError
        );
      }
    }

    return result;
  } catch (error) {
    // Middleware errors should never break the application
    console.error("AppSignal Middleware: Unexpected error", error);

    // Still need to call next to continue the middleware chain
    return next(action);
  }
};

export default appsignalMiddleware;
